import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import noImage from "../assets/images/noImage.png";
import "../assets/css/familyClans.css";
import "../assets/css/userReferrals.css";

const numberFormat = new Intl.NumberFormat();
const rangeFormat = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "custom", label: "Custom range" },
];

const describeRange = (period) => {
  if (!period?.startDate) return null;
  const start = rangeFormat.format(new Date(period.startDate));
  if (!period.endDate) return `${start} – now`;
  const inclusiveEnd = new Date(new Date(period.endDate).getTime() - 1);
  return `${start} – ${rangeFormat.format(inclusiveEnd)}`;
};

const UserReferrals = () => {
  const location = useLocation();
  const initialPeriod = useMemo(() => {
    const query = new URLSearchParams(location.search);
    const selectedPeriod = query.get("period") || "today";
    const startDate = query.get("startDate") || "";
    const endDate = query.get("endDate") || "";
    return {
      period: selectedPeriod,
      startDate,
      endDate,
      applied:
        selectedPeriod === "custom" && startDate && endDate
          ? { period: "custom", startDate, endDate }
          : { period: selectedPeriod },
    };
  }, [location.search]);
  const [period, setPeriod] = useState(initialPeriod.period);
  const [customStartDate, setCustomStartDate] = useState(initialPeriod.startDate);
  const [customEndDate, setCustomEndDate] = useState(initialPeriod.endDate);
  const [periodError, setPeriodError] = useState("");
  const [appliedPeriod, setAppliedPeriod] = useState(initialPeriod.applied);
  const [referrers, setReferrers] = useState([]);
  const [periodMeta, setPeriodMeta] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    axios
      .get("wallet/admin/referrals", {
        params: {
          start: page * rowsPerPage,
          limit: rowsPerPage,
          ...appliedPeriod,
        },
      })
      .then((response) => {
        if (!active) return;
        if (!response.data.status) throw new Error(response.data.message);
        setReferrers(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setPeriodMeta(response.data.period || null);
      })
      .catch((requestError) => {
        if (!active) return;
        setReferrers([]);
        setTotal(0);
        setError(
          requestError.response?.data?.message ||
            requestError.message ||
            "Unable to load user referrals.",
        );
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [appliedPeriod, page, rowsPerPage]);

  const handlePeriodChange = (event) => {
    const value = event.target.value;
    setPeriod(value);
    setPeriodError("");
    if (value !== "custom") {
      setPage(0);
      setAppliedPeriod({ period: value });
    }
  };

  const applyCustomRange = (event) => {
    event.preventDefault();
    if (!customStartDate || !customEndDate) {
      setPeriodError("Choose both a start date and an end date.");
      return;
    }
    if (customStartDate > customEndDate) {
      setPeriodError("The start date cannot be after the end date.");
      return;
    }
    setPeriodError("");
    setPage(0);
    setAppliedPeriod({
      period: "custom",
      startDate: customStartDate,
      endDate: customEndDate,
    });
  };

  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const firstEntry = total ? page * rowsPerPage + 1 : 0;
  const lastEntry = Math.min((page + 1) * rowsPerPage, total);
  const rangeLabel = describeRange(periodMeta);
  const referralQuery = new URLSearchParams(appliedPeriod).toString();

  return (
    <section className="family-clans-page user-referrals-page" aria-labelledby="user-referrals-title">
      <div className="page-title">
        <div className="row align-items-end">
          <div className="col-12 col-md-7">
            <h3 id="user-referrals-title" className="mb-2 text-white">
              User Referrals
            </h3>
          </div>
          <div className="col-12 col-md-5">
            <nav aria-label="breadcrumb" className="breadcrumb-header float-md-end">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/admin/dashboard" className="text-danger">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">User Referrals</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="card family-clans-card">
        <div className="card-header family-clans-toolbar">
          <div>
            <span className="family-clans-count">{numberFormat.format(total)}</span>
            <span className="family-clans-count-label"> users with referrals</span>
          </div>
          <label className="family-clans-period">
            <span>Referral period</span>
            <select className="form-control" value={period} onChange={handlePeriodChange}>
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        {period === "custom" && (
          <form className="family-clan-roster-filters" onSubmit={applyCustomRange}>
            <label>
              <span>Start date</span>
              <input
                type="date"
                className="form-control"
                value={customStartDate}
                max={customEndDate || undefined}
                onChange={(event) => setCustomStartDate(event.target.value)}
              />
            </label>
            <label>
              <span>End date</span>
              <input
                type="date"
                className="form-control"
                value={customEndDate}
                min={customStartDate || undefined}
                onChange={(event) => setCustomEndDate(event.target.value)}
              />
            </label>
            <div className="family-clan-roster-filter-actions">
              <button type="submit" className="btn btn-sm btn-primary">Show referrals</button>
            </div>
          </form>
        )}

        {(rangeLabel || periodError) && (
          <div className="family-clans-period-summary">
            {periodError ? (
              <span className="text-danger">{periodError}</span>
            ) : (
              <span>Referrals recorded from {rangeLabel}</span>
            )}
          </div>
        )}

        <div className="card-body card-overflow p-0">
          <div className="table-responsive">
            <table className="table family-clans-table mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th>User ID</th>
                  <th>Referral count</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="4" className="family-clans-state">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Loading user referrals…
                  </td></tr>
                )}
                {!loading && error && (
                  <tr><td colSpan="4" className="family-clans-state text-danger">{error}</td></tr>
                )}
                {!loading && !error && referrers.map((user) => (
                  <tr key={user.userId}>
                    <td>
                      <div className="family-clan-identity">
                        <img
                          src={user.image || noImage}
                          onError={(event) => { event.currentTarget.src = noImage; }}
                          alt=""
                        />
                        <strong>{user.name || "Unnamed user"}</strong>
                      </div>
                    </td>
                    <td><code>{user.uniqueId || "—"}</code></td>
                    <td><strong>{numberFormat.format(user.referralCount || 0)}</strong></td>
                    <td className="text-end">
                      <Link
                        to={`/admin/userReferrals/${user.userId}?${referralQuery}`}
                        className="btn btn-sm family-clans-info"
                        aria-label={`View users referred by ${user.name || "this user"}`}
                      >
                        <i className="fas fa-user-friends" aria-hidden="true"></i>
                        <span>View referrals</span>
                      </Link>
                    </td>
                  </tr>
                ))}
                {!loading && !error && !referrers.length && (
                  <tr><td colSpan="4" className="family-clans-state">No referrals found for this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && !error && total > 0 && (
          <div className="card-footer family-clans-pagination">
            <span>Showing {firstEntry}–{lastEntry} of {total}</span>
            <div className="family-clans-page-controls">
              <label>
                Rows{" "}
                <select
                  className="form-select form-select-sm"
                  value={rowsPerPage}
                  onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </label>
              <button className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page">&lsaquo;</button>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)} aria-label="Next page">&rsaquo;</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default UserReferrals;
